import { useState } from "react";

import type {
  SchedulingCandidate,
  SchedulingCandidatesResponse
} from "../../api/statelessClient";
import type {
  InterventionPlan,
  ResourceRoomProjectDocumentV2
} from "../../storage/resourceRoomDocument";

const weekdayLabels = ["", "星期一", "星期二", "星期三", "星期四", "星期五"];
const natureLabels = {
  PREFERRED: "优先抽离",
  GENERAL: "一般",
  PROTECTED: "保护课程"
} as const;
const weekPatternLabels = {
  EVERY: "每周",
  ODD: "单周",
  EVEN: "双周"
} as const;
const conflictLabels: Record<string, string> = {
  SLOT_CAPACITY: "该时段已有 5 节并行课程",
  STUDENT_BUSY: "学生时间冲突",
  TEACHER_BUSY: "教师时间冲突",
  ROOM_BUSY: "教室时间冲突",
  SCHOOL_UNAVAILABLE: "全校不可排",
  TEACHER_UNAVAILABLE: "教师不可排",
  ROOM_UNAVAILABLE: "教室不可排"
};

export function CandidatePicker({
  document,
  plan,
  result,
  busy,
  onChoose,
  onMore,
  onClose
}: {
  document: ResourceRoomProjectDocumentV2;
  plan: InterventionPlan;
  result: SchedulingCandidatesResponse;
  busy: boolean;
  onChoose: (candidate: SchedulingCandidate, confirmProtected: boolean) => void;
  onMore: () => void;
  onClose: () => void;
}): React.JSX.Element {
  const [confirmed, setConfirmed] = useState<Record<string, boolean>>({});
  const [activeSignature, setActiveSignature] = useState(result.candidates[0]?.signature ?? "");
  const slotById = new Map(document.entities.time_slots.map((item) => [item.id, item]));
  const studentById = new Map(document.entities.students.map((item) => [item.id, item]));
  const slots = [...document.entities.time_slots].sort((left, right) => left.sort_order - right.sort_order);
  if (!result.candidates.length) {
    return (
      <section className="resource-panel" aria-live="polite">
        <h2>当前条件下没有完整方案</h2>
        <p>还缺 {result.missing_session_count} 个时段。{result.conflict_categories.map((item) => conflictLabels[item] ?? item).join("、") || "可用时间不足"}。</p>
        <button className="secondary-button" type="button" onClick={onClose}>关闭</button>
      </section>
    );
  }
  const selectedCandidate = result.candidates.find(
    (candidate) => candidate.signature === activeSignature
  ) ?? result.candidates[0];
  const selectedIndex = result.candidates.indexOf(selectedCandidate);
  const hasProtected = selectedCandidate.protected_exposure_count > 0;
  const courseName = document.entities.intervention_courses.find(
    (course) => course.id === plan.course_id
  )?.name ?? "未命名干预课程";
  const participantNames = plan.student_ids.flatMap((studentId) => {
    const name = studentById.get(studentId)?.display_name;
    return name ? [name] : [];
  });
  const roomName = document.entities.rooms.find((room) => room.id === plan.room_id)?.name;
  const teacherName = document.entities.teachers.find(
    (teacher) => teacher.id === plan.main_teacher_id
  )?.name;
  const sessionByStartCell = new Map<string, {
    session: SchedulingCandidate["sessions"][number];
    orderedSlotIds: string[];
  }>();
  const coveredCells = new Set<string>();
  selectedCandidate.sessions.forEach((session) => {
    const orderedSlotIds = session.time_slot_ids
      .filter((slotId) => slotById.has(slotId))
      .sort((left, right) => (
        (slotById.get(left)?.sort_order ?? 0) - (slotById.get(right)?.sort_order ?? 0)
      ));
    const firstSlotId = orderedSlotIds[0];
    if (!firstSlotId) return;
    sessionByStartCell.set(`${session.weekday}:${firstSlotId}`, { session, orderedSlotIds });
    orderedSlotIds.slice(1).forEach((slotId) => {
      coveredCells.add(`${session.weekday}:${slotId}`);
    });
  });

  return (
    <section className="resource-panel" aria-labelledby="candidate-title">
      <div className="page-header resource-candidate-header">
        <div><h2 id="candidate-title">选择一套完整方案</h2></div>
        <button className="text-button" type="button" onClick={onClose}>关闭</button>
      </div>
      <div className="resource-candidate-tabs" role="group" aria-label="切换候选方案">
        {result.candidates.map((candidate, index) => (
          <button
            className="resource-candidate-tab"
            type="button"
            key={candidate.signature}
            aria-pressed={candidate.signature === selectedCandidate.signature}
            onClick={() => setActiveSignature(candidate.signature)}
          >
            <strong>方案 {index + 1}</strong>
            <span>覆盖 {candidate.distinct_day_count} 天 · 保护 {candidate.protected_exposure_count} 格</span>
          </button>
        ))}
      </div>
      <section
        className={`resource-candidate-preview${hasProtected ? " is-protected" : ""}`}
        aria-labelledby="candidate-preview-title"
      >
        <div className="resource-candidate-preview-header">
          <div>
            <h3 id="candidate-preview-title">方案 {selectedIndex + 1} 周课表</h3>
          </div>
          <div className="resource-candidate-meta" aria-label="当前方案统计">
            <span>覆盖 {selectedCandidate.distinct_day_count} 天</span>
            <span>保护课程格 {selectedCandidate.protected_exposure_count}</span>
            <span>一般课程格 {selectedCandidate.general_exposure_count}</span>
          </div>
        </div>

        <div
          className="resource-table-scroll resource-candidate-table-scroll"
          tabIndex={0}
          aria-label={`方案 ${selectedIndex + 1} 周课表，可横向滚动查看更多`}
        >
          <table className="resource-candidate-timetable">
            <caption className="visually-hidden">方案 {selectedIndex + 1}：{courseName}候选周课表</caption>
            <thead>
              <tr>
                <th scope="col">节次</th>
                {[1, 2, 3, 4, 5].map((weekday) => (
                  <th scope="col" key={weekday}>{weekdayLabels[weekday]}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {slots.map((slot) => (
                <tr key={slot.id}>
                  <th scope="row">{slot.raw_label}</th>
                  {[1, 2, 3, 4, 5].map((weekday) => {
                    const cellKey = `${weekday}:${slot.id}`;
                    if (coveredCells.has(cellKey)) return null;
                    const scheduled = sessionByStartCell.get(cellKey);
                    if (!scheduled) {
                      return <td className="resource-candidate-empty-cell" key={weekday} />;
                    }
                    const exposureRows = plan.student_ids.flatMap((studentId) => {
                      const details = selectedCandidate.exposures
                        .filter((exposure) => (
                          exposure.session_index === scheduled.session.session_index &&
                          exposure.student_id === studentId
                        ))
                        .map((exposure) => (
                          `${exposure.original_course_name ?? "无原班课程"}${
                            exposure.nature ? `（${natureLabels[exposure.nature]}）` : ""
                          }`
                        ));
                      const studentName = studentById.get(studentId)?.display_name;
                      return studentName && details.length
                        ? [{ studentName, details: Array.from(new Set(details)) }]
                        : [];
                    });
                    return (
                      <td
                        className="resource-candidate-session-cell"
                        data-weekday={weekday}
                        data-start-slot-id={slot.id}
                        key={weekday}
                        rowSpan={scheduled.orderedSlotIds.length}
                      >
                        <article className="resource-candidate-lesson">
                          <div className="resource-candidate-lesson-heading">
                            <strong>{courseName}</strong>
                            <span>{plan.lesson_type === "INDIVIDUAL" ? "个训" : "小组"} · {weekPatternLabels[plan.week_pattern]}</span>
                          </div>
                          <p>{participantNames.join("、")}</p>
                          {roomName || teacherName ? (
                            <p className="resource-candidate-lesson-support">
                              {[roomName, teacherName].filter(Boolean).join(" · ")}
                            </p>
                          ) : null}
                          <div className="resource-candidate-original-courses">
                            <span>原班抽离</span>
                            {exposureRows.map((row) => (
                              <p key={row.studentName}><strong>{row.studentName}</strong>：{row.details.join("、")}</p>
                            ))}
                          </div>
                        </article>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {hasProtected ? (
          <label className="resource-candidate-confirmation">
            <input
              type="checkbox"
              checked={Boolean(confirmed[selectedCandidate.signature])}
              onChange={(event) => setConfirmed({
                ...confirmed,
                [selectedCandidate.signature]: event.target.checked
              })}
            />
            <span>我已核对课表中的保护课程抽离安排</span>
          </label>
        ) : null}

        <div className="resource-candidate-actions">
          <button
            className="primary-button"
            type="button"
            disabled={busy || (hasProtected && !confirmed[selectedCandidate.signature])}
            onClick={() => onChoose(selectedCandidate, hasProtected)}
          >
            选择这套方案
          </button>
          {result.has_more ? (
            <button className="secondary-button" type="button" disabled={busy} onClick={onMore}>
              换一批候选时段
            </button>
          ) : null}
        </div>
      </section>
    </section>
  );
}
